import { AlertCircle, Inbox, Loader2, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { pickErrorMessage } from '@/shared/api/errorMessage';
import { isApiError } from '@/shared/api/errors';
import { cn } from '@/shared/lib/cn';
import { Button } from './Button';

/* ───── Video cards ───── */

/**
 * The grid every video card sits in (a creator's videos, the Download results, the projects).
 * Thumbnails are square: the videos are mostly vertical, and a 9:16 card was taller than the
 * screen could show two rows of; a square keeps the subject in view at half the height.
 */
export const VIDEO_CARD_GRID =
  'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';
export const VIDEO_THUMBNAIL = 'aspect-square';

/* ───── Skeletons ───── */

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('skeleton', className)} aria-hidden {...props} />;
}

/** A skeleton shaped like the content it replaces, not a generic spinner. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} aria-hidden>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className={cn('h-3.5', index === lines - 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  );
}

/** `video`: shaped like the video cards it stands in for, so nothing jumps when they arrive. */
export function SkeletonCardGrid({ count = 6, variant = 'default' }: { count?: number; variant?: 'default' | 'video' }) {
  const video = variant === 'video';
  return (
    <div
      className={
        video ? VIDEO_CARD_GRID : 'grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
      }
    >
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="overflow-hidden rounded-lg border border-border bg-surface">
          <Skeleton className={cn('w-full rounded-none', video ? VIDEO_THUMBNAIL : 'aspect-video')} />
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex gap-4 border-b border-border bg-bg-subtle px-4 py-2.5">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 border-b border-border px-4 py-3 last:border-0">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} className="h-3.5 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ───── Empty ───── */

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
  compact?: boolean;
}

/** An empty state always offers the next step — a blank panel is a dead end. */
export function EmptyState({ icon, title, description, action, className, compact }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed border-border px-6 text-center',
        compact ? 'py-8' : 'py-14',
        className,
      )}
    >
      <div className="mb-3 rounded-full bg-surface-hover p-3 text-fg-subtle">
        {icon ?? <Inbox className="h-5 w-5" />}
      </div>
      <p className="text-base font-medium text-fg">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-fg-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/* ───── Error ───── */

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  onReport?: (context: string) => void;
  className?: string;
  compact?: boolean;
}

/**
 * Errors show the localized message for the code plus the code itself, so a user reporting a
 * problem can quote something we can search for.
 */
export function ErrorState({ error, onRetry, onReport, className, compact }: ErrorStateProps) {
  const { t } = useTranslation();
  const apiError = isApiError(error) ? error : null;
  const code = apiError?.code ?? 'INTERNAL_ERROR';

  const localized = t(`errors.${code}`, { defaultValue: '' });
  const message = localized || apiError?.message || t('errors.INTERNAL_ERROR');
  const offline = code === 'NETWORK_ERROR';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-danger/30 bg-danger-subtle/40 px-6 text-center',
        compact ? 'py-6' : 'py-12',
        className,
      )}
      role="alert"
    >
      <div className="mb-3 rounded-full bg-danger/10 p-3 text-danger">
        {offline ? <WifiOff className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
      </div>
      <p className="text-base font-medium text-fg">
        {offline ? t('states.offline') : t('errors.genericTitle')}
      </p>
      <p className="mt-1 max-w-md text-sm text-fg-muted">{message}</p>
      <p className="mt-2 font-mono text-2xs text-fg-subtle">{t('common.errorCode', { code })}</p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        )}
        {onReport && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onReport(`${code}: ${apiError?.message ?? String(error)}`)}
          >
            {t('common.reportIssue')}
          </Button>
        )}
      </div>
    </div>
  );
}

/* ───── Boundary ───── */

export interface QueryBoundaryProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty?: boolean;
  onRetry?: () => void;
  onReport?: (context: string) => void;
  loadingFallback?: React.ReactNode;
  emptyFallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The four states, in one place, so no screen can accidentally ship only two of them.
 */
export function QueryBoundary({
  isLoading,
  isError,
  error,
  isEmpty,
  onRetry,
  onReport,
  loadingFallback,
  emptyFallback,
  children,
}: QueryBoundaryProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label={t('a11y.loading')}>
        {loadingFallback ?? <SkeletonText lines={4} />}
      </div>
    );
  }
  if (isError) return <ErrorState error={error} onRetry={onRetry} onReport={onReport} />;
  if (isEmpty) return <>{emptyFallback ?? <EmptyState title={t('states.emptyTitle')} />}</>;
  return <>{children}</>;
}

/* ───── Inline spinner ───── */

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-4 w-4 animate-spin text-fg-subtle', className)} aria-hidden />;
}

/** Map any thrown value to a user-facing sentence. */
export function useErrorMessage() {
  const { t } = useTranslation();
  return (error: unknown): string =>
    pickErrorMessage(error, (code) => t(`errors.${code}`, { defaultValue: '' }));
}
